export const ELIGIBILITY_PATH = {
  RECIPROCITY: "RECIPROCITY",
  RECIPROCITY_APPROVED_PENDING_ELECTION:
    "RECIPROCITY_APPROVED_PENDING_ELECTION",
}

export const RECIPROCITY_APPLICATION_STATE = {
  DRAFT: "draft",
  IN_REVIEW: "in_review",
  APPROVED: "approved",
  ELECTED: "elected",
  DENIED: "denied",
}

const IN_REVIEW_STATUSES = new Set([
  "submitted",
  "under_review",
  "changes_requested",
])

// How long the elected and denied-after-approval states stay on the dashboard.
// Both are terminal, so without a limit a lapsed Member or a later Associate
// would keep seeing them.
export const TERMINAL_STATE_DAYS = 90
const DAY_MS = 24 * 60 * 60 * 1000

const isRecent = (timestamp, now) =>
  now - (Date.parse(timestamp) || 0) < TERMINAL_STATE_DAYS * DAY_MS

const lower = value => String(value || "").toLowerCase()

const latestByUpdatedAt = applications =>
  applications.reduce((latest, application) => {
    if (!latest) return application
    const latestUpdated = Date.parse(latest.updatedAt || 0) || 0
    const updated = Date.parse(application.updatedAt || 0) || 0
    return updated > latestUpdated ? application : latest
  }, null)

/**
 * Turns the person's Member applications into the one reciprocity application
 * state the dashboard shows, or null when there is nothing to show.
 *
 * A draft carries no `reciprocityType` until submit stamps it, so it counts
 * only while the authorization is still unused (`RECIPROCITY`). Every later
 * state requires the stamped type, which keeps ordinary Member applications
 * (graduates, Associates, restorations) off this surface.
 *
 * `canReapply` is true only for a denial before approval: the grant is still
 * unused. After approval it is consumed, so staff must record a new one.
 *
 * The elected state and a denial after approval are terminal and show for
 * `TERMINAL_STATE_DAYS` from `electedAt` and `updatedAt` respectively. An
 * approved application with no `electedAt` shows only while the path is
 * `RECIPROCITY_APPROVED_PENDING_ELECTION`.
 */
export const resolveReciprocityApplication = ({
  eligibilityPath,
  applications,
  now = Date.now(),
}) => {
  const memberApplications = (applications || []).filter(
    application => lower(application?.applicationType) === "member",
  )
  const latest = latestByUpdatedAt(memberApplications)
  if (!latest) return null

  const status = lower(latest.status)
  const authorizationUnused = eligibilityPath === ELIGIBILITY_PATH.RECIPROCITY
  const base = {
    applicationId: latest.id,
    electionDate: latest.electionDate || null,
    canReapply: false,
  }

  if (status === "draft") {
    return authorizationUnused
      ? { ...base, state: RECIPROCITY_APPLICATION_STATE.DRAFT }
      : null
  }

  if (!latest.reciprocityType) return null

  if (IN_REVIEW_STATUSES.has(status)) {
    return { ...base, state: RECIPROCITY_APPLICATION_STATE.IN_REVIEW }
  }

  if (status === "approved") {
    if (!latest.electedAt) {
      // Only while the backend still reports it as awaiting election, so an
      // approval that is never elected (or later withdrawn) does not linger.
      return eligibilityPath ===
        ELIGIBILITY_PATH.RECIPROCITY_APPROVED_PENDING_ELECTION
        ? { ...base, state: RECIPROCITY_APPLICATION_STATE.APPROVED }
        : null
    }
    return isRecent(latest.electedAt, now)
      ? { ...base, state: RECIPROCITY_APPLICATION_STATE.ELECTED }
      : null
  }

  if (status === "denied") {
    if (!authorizationUnused && !isRecent(latest.updatedAt, now)) return null
    return {
      ...base,
      state: RECIPROCITY_APPLICATION_STATE.DENIED,
      canReapply: authorizationUnused,
    }
  }

  return null
}

/**
 * True when the application card replaces the Associate / Affiliate chooser and
 * requirements tracker. A denial after approval cannot reapply through
 * reciprocity, but the other membership routes may still be open, so those
 * stay visible next to the card.
 */
export const replacesPathToMembership = application =>
  Boolean(application) &&
  !(
    application.state === RECIPROCITY_APPLICATION_STATE.DENIED &&
    !application.canReapply
  )
